import { Center, Group, Loader, Stack } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAnalysis } from '@/api/analyses';
import { errorMessage } from '@/api/errors';
import { useProject } from '@/api/projects';
import { useProjectReports } from '@/api/reports';
import { projectViewerLink, projectViewerToken } from '@/api/viewer';
import { EmptyState } from '@/components/EmptyState';
import { ButtonLink } from '@/components/links';
import { SectionDivider } from '@/components/SectionDivider';
import { usePageTitle } from '@/components/usePageTitle';
import { OutputsTable } from '@/features/analyses/OutputsTable';
import { ClientShell } from '@/shells/ClientShell';

export const Route = createFileRoute('/_client/projects_/$projectId/analyses/$analysisId')({ component: ClientAnalysisPage });

function ClientAnalysisPage() {
  const { t, i18n } = useTranslation('analyses');
  const { t: tp } = useTranslation('projects');
  const { projectId, analysisId } = Route.useParams();
  const project = useProject(projectId);
  const a = useAnalysis(analysisId);
  const reports = useProjectReports(projectId);
  const delivered = a.data?.client_status === 'results_ready';
  // One viewer credential for the whole table: it says which outputs are bucket files, and where.
  const exportable = (a.data?.outputs ?? []).filter((o) => o.is_deliverable && !o.deleted).map((o) => o.public_id);
  const credential = useQuery({
    queryKey: ['viewer-token', projectId, exportable],
    queryFn: () => projectViewerToken(projectId, exportable),
    enabled: !!delivered && exportable.length > 0,
    staleTime: 10 * 60_000,
    retry: false,
  });
  const lang = i18n.language as 'cs' | 'en';
  usePageTitle(a.data ? a.data.name[lang] || a.data.name.cs : t('title'));
  const name = a.data ? a.data.name[lang] || a.data.name.cs : '…';
  return (
    <ClientShell
      eyebrow={project.data?.name}
      title={name}
      lead={a.data ? tp(`status.analysis.${a.data.client_status}`, { defaultValue: a.data.client_status }) : undefined}
    >
      <Stack gap="lg">
        {a.isPending ? (
          <Center py="xl">
            <Loader color="forest" />
          </Center>
        ) : a.isError ? (
          <EmptyState title={errorMessage(a.error)} />
        ) : a.data.client_status === 'results_ready' ? (
          <>
            <SectionDivider label={t('clientPage.results')} />
            <OutputsTable
              outputs={a.data.outputs ?? []}
              projectId={projectId}
              reports={reports.data}
              delivered
              granted={credential.data?.objects}
              fetchLink={(object, path) => projectViewerLink(projectId, object, path)}
            />
          </>
        ) : (
          <EmptyState title={t('clientPage.processing')} description={t('clientPage.processingHint')} />
        )}
        <Group>
          <ButtonLink variant="default" to="/projects/$projectId" params={{ projectId }}>
            {t('clientPage.back')}
          </ButtonLink>
        </Group>
      </Stack>
    </ClientShell>
  );
}
